import { useCallback, useEffect, useState, type SyntheticEvent } from 'react';
import { Link } from 'react-router-dom';
import { FileCode2, FileDiff, ShieldAlert, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import type { PatchCheck, PatchRequest } from '@shared/patch-request';
import { PATCH_NOTE_MAX_CHARS, diffFilePath, parseUnifiedDiff } from '@shared/patch-request';
import { AppAlert } from '@/components/AppAlert';
import { ExternalLink } from '@/components/ExternalLink';
import { EmptyState } from '@/components/EmptyState';
import { FormField } from '@/components/FormField';
import { LoadError } from '@/components/LoadError';
import { LoadingState } from '@/components/LoadingState';
import { PatchDiff } from '@/components/PatchDiff';
import { RecordDialog } from '@/components/RecordDialog';
import { SelectableList } from '@/components/SelectableList';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { useLoader } from '@/hooks/use-loader';
import { useIsAdmin } from '@/hooks/usePermission';
import { describeApiError } from '@/lib/api-errors';
import { useCurrentProject } from '@/lib/current-project';
import { MACHINE_READ_TIME_LIMIT_MS, limitApiCall, withTimeLimit } from '@/lib/time-limit';
import { formatRelative } from '@/lib/formatters';
import {
  applyAllTaskPatchRequests,
  applyTaskPatchRequest,
  checkTaskPatchRequest,
  explainTaskPatchRequest,
  getTaskPatchRequests,
  rejectAllTaskPatchRequests,
  rejectTaskPatchRequest,
} from '@/lib/tasks-api';
import { cn } from '@/lib/utils';
import { PATCH_APPLY_TIME_LIMIT_MS, footerState, reloadWithin, retryableAt, settleDelay, timedOutNote } from './patch-apply';
import { PATCH_STATUS_META } from './patch-status';
import { useTaskLiveReload } from './use-task-live-reload';

/**
 * T-57: the coding agent's patch requests on a task (shared/patch-request.ts): changes the guards keep
 * it from making (agent instructions, CI, devkit config, pinned scripts). The card shows a button while
 * one waits; its dialog shows the diff beside the AI's explanation and the agent's reason, and a factory
 * admin applies it (committed and pushed in the run's worktree) or rejects it with a note.
 * SF-T-107: the dialog lists every file of every request down its side, and while more than one waits
 * an admin applies them all as one commit and one push (Apply all), or rejects them all with one note.
 * SF-T-153: the open dialog reads them again on a live update for its task, so a decision shows even when
 * its answer is lost (or another admin made it); Apply stops waiting after PATCH_APPLY_TIME_LIMIT_MS and
 * says where the requests stand instead.
 */

/** Reads the task's patch requests again, answering them (null when they couldn't be read). */
type ReloadRequests = () => Promise<PatchRequest[] | null>;

/** Stops a press on the button (or in its dialog) from opening, picking or dragging the card. */
const stop = (e: SyntheticEvent) => e.stopPropagation();
const STOP_PROPS = { onClick: stop, onMouseDown: stop, onPointerDown: stop, onTouchStart: stop, onKeyDown: stop };

/** The card's button while patch requests wait (`count`), opening their dialog. */
export function TaskCardPatchButton({ taskId, count }: { taskId: string; count: number }) {
  const [open, setOpen] = useState(false);
  if (count < 1) return null;
  return (
    <div data-no-row-click="" {...STOP_PROPS}>
      <Button type="button" size="xs" variant="outline" data-hint="The AI builder asks for a change it isn't allowed to make itself" onClick={() => setOpen(true)}>
        <FileDiff className="text-warning" />
        {count === 1 ? 'Patch request' : `${count} patch requests`}
      </Button>
      {open ? <PatchRequestDialog taskId={taskId} open={open} onClose={() => setOpen(false)} /> : null}
    </div>
  );
}

/** Applied, but the push failed: Push again sends it. */
const unpushed = (p: PatchRequest) => p.status === 'applied' && p.pushed === false;

/** The ones still needing an admin first (as the card counts them), then newest. */
function ordered(list: PatchRequest[]): PatchRequest[] {
  const rank = (p: PatchRequest) => (p.status === 'pending' || p.status === 'applying' || unpushed(p) ? 0 : 1);
  return [...list].sort((a, b) => rank(a) - rank(b) || b.created_at.localeCompare(a.created_at));
}

export function PatchRequestDialog({
  taskId,
  open,
  onClose,
  initialId = null,
  preview,
}: {
  taskId: string;
  open: boolean;
  onClose: () => void;
  /** The request to show first (the task pop-up's row); else the first still waiting. */
  initialId?: string | null;
  /** Show these instead of loading the task's (the /dev/ui gallery). */
  preview?: PatchRequest[];
}) {
  const projectId = useCurrentProject()?.id;
  const loader = useLoader((signal) => getTaskPatchRequests(taskId, { signal }), [taskId], {
    key: `${projectId ?? 'none'}:task-patches:${taskId}`,
    enabled: open && !preview,
    // The explanation is made in the background, and an Apply the browser stopped waiting on may still
    // be landing: look again until they're settled (the live update usually comes first).
    pollMs: 4_000,
    pollWhile: (res) => !!res?.data?.patch_requests.some((p) => p.status === 'applying' || (p.status === 'pending' && !p.explanation && !p.explanation_error)),
    pollLimit: 20,
  });
  // One reload for a live update on this task and for an Apply the browser stopped waiting on (SF-T-153).
  const { refresh } = loader;
  const reloadRequests = useCallback<ReloadRequests>(
    async () => (preview ? null : ((await refresh().catch(() => null))?.data?.patch_requests ?? null)),
    [preview, refresh]
  );
  useTaskLiveReload(taskId, open && !preview, reloadRequests);
  // An Apply the machine never answered turns retryable when its hold ends (5 minutes), and nothing comes
  // on the hub then: read again just after, so Apply again shows without reopening the dialog.
  const settleAt = open && !preview ? retryableAt(loader.data?.data?.patch_requests ?? []) : null;
  const readAt = loader.updatedAt;
  useEffect(() => {
    if (settleAt === null) return undefined;
    const timer = setTimeout(() => void reloadRequests(), settleDelay(settleAt, Date.now()));
    return () => clearTimeout(timer);
  }, [settleAt, readAt, reloadRequests]);
  const list = ordered(preview ?? loader.data?.data?.patch_requests ?? []);
  const [picked, setPicked] = useState<{ id: string | null; file: number }>({ id: initialId, file: 0 });
  const current = list.find((p) => p.id === picked.id) ?? list[0] ?? null;
  const waiting = list.filter((p) => p.status === 'pending');
  // Whether each waiting one still applies to the branch as it is now: asked of the machine when the dialog opens.
  const checkKey = waiting.map((p) => `${p.id}@${p.updated_at}`).join(',');
  const checker = useLoader(
    async (signal) => {
      // A check that never answers counts as unknown, so it can't hold Apply all off for good (SF-T-153).
      const answers = await Promise.all(
        waiting.map((p) => limitApiCall((limited) => checkTaskPatchRequest(taskId, p.id, { signal: limited }), MACHINE_READ_TIME_LIMIT_MS, signal))
      );
      return Object.fromEntries(waiting.map((p, i) => [p.id, answers[i]?.data?.check ?? null])) as Record<string, PatchCheck | null>;
    },
    [taskId, checkKey],
    { enabled: open && waiting.length > 0 && !preview }
  );
  const checkOf = (p: PatchRequest): PatchCheck | 'checking' | null => (p.status !== 'pending' || preview ? null : checker.loading ? 'checking' : (checker.data?.[p.id] ?? null));
  const check = current ? checkOf(current) : null;
  const loadError = loader.data?.error ? describeApiError(loader.data, 'Try again in a moment.') : null;
  const fileCount = list.reduce((n, p) => n + Math.max(p.files.length, 1), 0);
  const file = current?.id === picked.id ? picked.file : 0;

  const replace = (...next: PatchRequest[]) =>
    loader.setData((res) => (res?.data ? { ...res, data: { patch_requests: res.data.patch_requests.map((p) => next.find((n) => n.id === p.id) ?? p) } } : res!));
  // The ones that no longer apply, by their number in the list: Apply all stays off while any does.
  const stale = waiting.filter((p) => {
    const c = checkOf(p);
    return c !== 'checking' && c?.state === 'stale';
  });
  const staleReason = stale.length
    ? `${stale.map((p) => `${list.indexOf(p) + 1}. ${p.files[0] ?? 'patch'}`).join(', ')} no longer ${stale.length === 1 ? 'applies' : 'apply'} to the branch: reject ${stale.length === 1 ? 'it' : 'them'} first, or check again`
    : null;

  return (
    <RecordDialog
      open={open}
      onClose={onClose}
      size="full"
      tall
      title={waiting.length > 1 ? `${waiting.length} patch requests from the AI builder` : 'Patch request from the AI builder'}
      description={
        waiting.length > 1
          ? `The agent asks for changes it isn't allowed to make itself. Apply all commits them together and pushes once to ${waiting[0]!.branch}, then tells the agent.`
          : current
            ? `${current.run_number ? `R-${current.run_number}'s agent` : 'The agent'} asks for a change it isn't allowed to make itself. Applying it commits and pushes it to ${current.branch}, then tells the agent.`
            : "Changes the AI builder asked for that it isn't allowed to make itself."
      }
      recordKey={current?.id ?? null}
      errorTitle="Couldn't show this patch request"
      footer={
        current ? (
          waiting.length > 1 && current.status === 'pending' ? (
            <PatchBatchActions
              key={waiting.map((p) => p.id).join(',')}
              taskId={taskId}
              requests={waiting}
              current={current}
              currentNumber={list.indexOf(current) + 1}
              staleReason={checker.loading && !preview ? 'Checking they still apply…' : staleReason}
              onChanged={replace}
              reload={reloadRequests}
              onClose={onClose}
            />
          ) : (
            <PatchActions
              key={current.id}
              taskId={taskId}
              request={current}
              stale={check !== 'checking' && check?.state === 'stale'}
              onChanged={replace}
              reload={reloadRequests}
              onClose={onClose}
            />
          )
        ) : null
      }
    >
      {!preview && loader.loading && !loader.data ? (
        <LoadingState inline />
      ) : !preview && loadError ? (
        <LoadError compact title="Couldn't load the patch requests" error={loadError} onRetry={loader.reload} />
      ) : !current ? (
        <EmptyState size="compact" title="No patch requests" description="The AI builder hasn't asked for any changes on this task." />
      ) : fileCount > 1 ? (
        <div className="grid gap-4 lg:grid-cols-[16rem_minmax(0,1fr)]">
          <PatchFileList requests={list} current={{ id: current.id, file }} checkOf={checkOf} onPick={(id, f) => setPicked({ id, file: f })} />
          <PatchRequestView taskId={taskId} request={current} file={file} onChanged={replace} check={check} onCheckAgain={checker.reload} />
        </div>
      ) : (
        <PatchRequestView taskId={taskId} request={current} onChanged={replace} check={check} onCheckAgain={checker.reload} />
      )}
    </RecordDialog>
  );
}

/**
 * SF-T-107: every file of every request in the dialog, grouped by request (numbered, with its run, status
 * and whether it still applies), as a SelectableList. Picking a file shows its diff, beside its own
 * request's explanation and reason.
 */
export function PatchFileList({
  requests,
  current,
  checkOf = () => null,
  onPick,
}: {
  /** In the dialog's order (ordered: the waiting ones first). */
  requests: PatchRequest[];
  current: { id: string; file: number };
  checkOf?: (p: PatchRequest) => PatchCheck | 'checking' | null;
  onPick: (id: string, file: number) => void;
}) {
  const groups = requests.map((p, i) => {
    const check = checkOf(p);
    const state = check === 'checking' ? 'checking' : check?.state === 'stale' ? 'no longer applies' : null;
    const status = unpushed(p) ? 'Not pushed' : PATCH_STATUS_META[p.status].label;
    return {
      label: `${i + 1}. ${[p.run_number ? `R-${p.run_number}` : null, status, state].filter(Boolean).join(' · ')}`,
      items: parseUnifiedDiff(p.diff).map((f, n) => {
        const path = diffFilePath(f);
        return {
          value: `${p.id}:${n}`,
          textValue: path,
          hint: path,
          leading: <FileCode2 className="size-3.5 text-muted-foreground" aria-hidden />,
          label: <span className="font-mono text-xs">{path}</span>,
          trailing: (
            <span className="font-mono text-xs tabular-nums">
              <span className="text-success-strong">+{f.added}</span> <span className="text-danger-strong">−{f.removed}</span>
            </span>
          ),
        };
      }),
    };
  });
  return (
    <SelectableList
      aria-label="Files in the patch requests"
      className="lg:sticky lg:top-0 lg:self-start"
      groups={groups}
      value={`${current.id}:${current.file}`}
      onValueChange={(v) => {
        const at = v.lastIndexOf(':');
        onPick(v.slice(0, at), Number(v.slice(at + 1)));
      }}
    />
  );
}

/**
 * SF-T-107: the dialog's footer while more than one request waits (factory admins): Apply all (one commit,
 * one push), Reject all with one note, or Reject this one (the request on show) so the rest can be applied.
 */
export function PatchBatchActions({
  taskId,
  requests,
  current,
  currentNumber,
  staleReason = null,
  onChanged,
  reload,
  onClose,
}: {
  taskId: string;
  /** Every waiting request: Apply all and Reject all take exactly these. */
  requests: PatchRequest[];
  /** The one on show, for Reject this one. */
  current: PatchRequest;
  currentNumber: number;
  /** Why Apply all is off (some no longer apply, or they're being checked). */
  staleReason?: string | null;
  onChanged: (...p: PatchRequest[]) => void;
  /** Reads the requests again when Apply all stops waiting (the dialog's reload). */
  reload?: ReloadRequests;
  onClose: () => void;
}) {
  const mayDecide = useIsAdmin();
  const [rejecting, setRejecting] = useState<'one' | 'all' | null>(null);
  const [busy, setBusy] = useState<'apply' | 'reject' | null>(null);
  const ids = requests.map((p) => p.id);

  if (!mayDecide) {
    return (
      <>
        <span className="mr-auto text-xs text-muted-foreground">An admin applies or rejects them.</span>
        <Button type="button" variant="outline" onClick={onClose}>
          Close
        </Button>
      </>
    );
  }

  const applyAll = async () => {
    setBusy('apply');
    const out = await withTimeLimit((signal) => applyAllTaskPatchRequests(taskId, ids, { signal }), PATCH_APPLY_TIME_LIMIT_MS);
    if (out.timedOut) {
      // Not a failure: they may well have landed. Stop waiting now, then say where they stand.
      setBusy(null);
      toast.info(timedOutNote(ids, await reloadWithin(reload)));
      return;
    }
    const res = out.res;
    setBusy(null);
    if (res.data) {
      onChanged(...res.data.patch_requests);
      toast.success(
        res.data.patch_requests.every((p) => p.pushed)
          ? `Applied ${ids.length} as one commit and pushed: the AI builder carries on`
          : 'Applied as one commit, but not pushed yet: the AI builder pushes it next time'
      );
      return;
    }
    toast.error(describeApiError(res, "Couldn't apply the patches"));
  };
  const reject = async (note: string) => {
    setBusy('reject');
    const res = rejecting === 'all' ? await rejectAllTaskPatchRequests(taskId, ids, note) : await rejectTaskPatchRequest(taskId, current.id, note);
    setBusy(null);
    if (res.data) {
      onChanged(...('patch_requests' in res.data ? res.data.patch_requests : [res.data.patch_request]));
      setRejecting(null);
      toast.success(rejecting === 'all' ? 'Rejected them all: the AI builder carries on without them' : 'Rejected: the AI builder carries on without it');
      return;
    }
    toast.error(describeApiError(res, "Couldn't reject the patch"));
  };

  if (rejecting) {
    return (
      <RejectNoteForm
        label={rejecting === 'all' ? `Note for the AI builder on all ${ids.length} (optional)` : `Note for the AI builder on ${currentNumber}. (optional)`}
        submitLabel={rejecting === 'all' ? `Reject all ${ids.length}` : 'Reject this one'}
        busy={busy === 'reject'}
        onCancel={() => setRejecting(null)}
        onSubmit={(note) => void reject(note)}
      />
    );
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="mr-auto"
        disabled={busy !== null}
        data-hint={`Reject only ${currentNumber}. ${current.files[0] ?? 'patch'}, the one on show; the rest stay waiting`}
        onClick={() => setRejecting('one')}
      >
        Reject this one…
      </Button>
      <Button type="button" variant="outline" disabled={busy !== null} onClick={() => setRejecting('all')}>
        Reject all…
      </Button>
      <Button type="button" loading={busy === 'apply'} loadingText="Applying…" disabled={!!staleReason} disabledReason={staleReason} onClick={() => void applyAll()}>
        Apply all {ids.length} and push
      </Button>
    </>
  );
}

/** The reject footer: an optional note for the AI builder, Cancel, and the reject button. */
function RejectNoteForm({
  label = 'Note for the AI builder (optional)',
  submitLabel = 'Reject patch',
  busy,
  onCancel,
  onSubmit,
}: {
  label?: string;
  submitLabel?: string;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (note: string) => void;
}) {
  const [note, setNote] = useState('');
  return (
    <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-end">
      <FormField label={label} className="min-w-0 flex-1">
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={PATCH_NOTE_MAX_CHARS} rows={2} placeholder="Why not, or what to do instead" />
      </FormField>
      <div className="flex shrink-0 justify-end gap-2">
        <Button type="button" variant="outline" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" variant="destructive" loading={busy} loadingText="Rejecting…" onClick={() => onSubmit(note)}>
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}

/** One request: the diff, and beside it its status, the AI's explanation and the agent's reason. */
export function PatchRequestView({
  taskId,
  request,
  file,
  onChanged,
  check = null,
  onCheckAgain,
}: {
  taskId: string;
  request: PatchRequest;
  /** Show only this file of its diff (the dialog's file list); else all of them. */
  file?: number;
  onChanged: (p: PatchRequest) => void;
  /** The stale check on a waiting request: its answer, or 'checking' while the machine looks. */
  check?: PatchCheck | 'checking' | null;
  onCheckAgain?: () => void;
}) {
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
      <div className="min-w-0">
        <PatchDiff diff={request.diff} only={file} />
      </div>
      <aside className="space-y-4 text-sm lg:sticky lg:top-0 lg:self-start">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge size="sm" meta={PATCH_STATUS_META[request.status]} />
          <span className="text-xs text-muted-foreground">asked {formatRelative(request.created_at)}</span>
        </div>
        <StaleCheck check={check} branch={request.branch} onCheckAgain={onCheckAgain} />
        {request.merged_pr_url && request.status !== 'rejected' ? (
          <AppAlert compact tone="warning" title="Its pull request already merged">
            {request.status === 'applied' ? 'It landed on' : 'Applying it lands on'} <code className="break-all">{request.branch}</code>, after{' '}
            <ExternalLink href={request.merged_pr_url} variant="underline" size="xs">
              its pull request
            </ExternalLink>{' '}
            merged, so it won't reach the base branch by itself. The AI builder is told to carry the commit over to a branch with an open pull request.
          </AppAlert>
        ) : null}
        {request.error ? (
          <AppAlert compact tone={request.status === 'applied' ? 'warning' : 'critical'}>
            {request.error}
          </AppAlert>
        ) : null}
        <Decision request={request} />
        <Explanation taskId={taskId} request={request} onChanged={onChanged} />
        <section className="space-y-1.5">
          <h3 className="text-xs font-medium text-muted-foreground">The agent's reason</h3>
          <blockquote className="border-l-2 pl-3 whitespace-pre-wrap break-words">{request.reason}</blockquote>
        </section>
      </aside>
    </div>
  );
}

/** Whether a waiting patch still applies to its branch (git.check_patch on the machine). */
function StaleCheck({ check, branch, onCheckAgain }: { check: PatchCheck | 'checking' | null; branch: string; onCheckAgain?: () => void }) {
  if (!check) return null;
  if (check === 'checking') {
    return (
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <Spinner size="xs" />
        Checking it still applies to {branch}…
      </p>
    );
  }
  const again = onCheckAgain ? (
    <Button type="button" size="xs" variant="outline" onClick={onCheckAgain}>
      Check again
    </Button>
  ) : null;
  if (check.state === 'stale') {
    return (
      <AppAlert compact tone="warning" title={`No longer applies to ${branch}`} action={again}>
        {check.detail ?? 'The branch has moved on since the agent asked.'} Reject it and ask the AI builder for a fresh one, or wait and check again.
      </AppAlert>
    );
  }
  if (check.state === 'unknown') {
    return (
      <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span>Couldn't check it still applies{check.detail ? `: ${check.detail}` : '.'}</span>
        {again}
      </p>
    );
  }
  return <p className="text-xs text-muted-foreground">Still applies cleanly to {branch}.</p>;
}

function Decision({ request }: { request: PatchRequest }) {
  if (request.status === 'applied') {
    return (
      <p className="text-xs text-muted-foreground">
        Applied by {request.decided_by?.name ?? 'an admin'}
        {request.decided_at ? ` ${formatRelative(request.decided_at)}` : ''} as <code className="font-mono">{request.commit_sha?.slice(0, 12)}</code>
        {request.pushed ? `, pushed to ${request.branch}` : ', not pushed yet'}.
      </p>
    );
  }
  if (request.status === 'rejected') {
    return (
      <div className="space-y-1 text-xs text-muted-foreground">
        <p>
          Rejected by {request.decided_by?.name ?? 'an admin'}
          {request.decided_at ? ` ${formatRelative(request.decided_at)}` : ''}.
        </p>
        {request.note ? <p className="whitespace-pre-wrap text-foreground">{request.note}</p> : null}
      </div>
    );
  }
  return null;
}

/** The AI's text with `backticked` names as inline code (it names files and settings that way). */
function WithCode({ text }: { text: string }) {
  return (
    <>
      {text.split(/(`[^`\n]+`)/).map((part, i) =>
        part.startsWith('`') && part.endsWith('`') && part.length > 2 ? (
          <code key={i} className="rounded-sm bg-muted px-1 font-mono text-[0.9em] break-all">
            {part.slice(1, -1)}
          </code>
        ) : (
          part
        )
      )}
    </>
  );
}

function Explanation({ taskId, request, onChanged }: { taskId: string; request: PatchRequest; onChanged: (p: PatchRequest) => void }) {
  const mayDecide = useIsAdmin();
  const [explaining, setExplaining] = useState(false);
  const explain = async () => {
    setExplaining(true);
    const res = await explainTaskPatchRequest(taskId, request.id);
    setExplaining(false);
    if (res.data) onChanged(res.data.patch_request);
    else toast.error(describeApiError(res, "Couldn't explain the patch"));
  };
  const again =
    mayDecide && request.status === 'pending' ? (
      <Button type="button" size="xs" variant="outline" loading={explaining} loadingText="Explaining…" onClick={() => void explain()}>
        Explain again
      </Button>
    ) : null;
  const e = request.explanation;
  return (
    <section className="space-y-2 rounded-md border border-ai-border bg-ai-soft p-3">
      <h3 className="flex items-center gap-1.5 text-xs font-medium text-ai-strong">
        <Sparkles className="size-3.5" aria-hidden />
        What it changes
      </h3>
      {e ? (
        <>
          <p className="leading-relaxed">
            <WithCode text={e.summary} />
          </p>
          {e.changes.length ? (
            <ul className="list-disc space-y-1 pl-4 text-xs">
              {e.changes.map((c, i) => (
                <li key={i} className="break-words">
                  <WithCode text={c} />
                </li>
              ))}
            </ul>
          ) : null}
          {e.risks.length ? (
            <AppAlert compact tone="warning" icon={<ShieldAlert />} title="Look twice at">
              <ul className="list-disc space-y-1 pl-4">
                {e.risks.map((r, i) => (
                  <li key={i} className="break-words">
                    <WithCode text={r} />
                  </li>
                ))}
              </ul>
            </AppAlert>
          ) : (
            <p className="text-xs text-muted-foreground">Nothing in it stood out as risky.</p>
          )}
          <div className="flex items-center justify-between gap-2">
            <span className="text-3xs text-muted-foreground">Written by AI from the diff alone; check it against the diff.</span>
            {again}
          </div>
        </>
      ) : request.explanation_error ? (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">{request.explanation_error}</p>
          {again}
        </div>
      ) : (
        <p className={cn('flex items-center gap-2 text-xs text-muted-foreground')}>
          <Spinner size="xs" />
          Explaining the patch…
        </p>
      )}
    </section>
  );
}

/** The dialog's footer for one request: Apply and push / Reject… for admins, Push again, Apply again, or Close. */
export function PatchActions({
  taskId,
  request,
  stale = false,
  onChanged,
  reload,
  onClose,
}: {
  taskId: string;
  request: PatchRequest;
  /** The machine says it no longer applies: Apply stays off (Reject still works). */
  stale?: boolean;
  onChanged: (p: PatchRequest) => void;
  /** Reads the requests again when Apply or Push stops waiting (the dialog's reload). */
  reload?: ReloadRequests;
  onClose: () => void;
}) {
  const mayDecide = useIsAdmin();
  const [rejecting, setRejecting] = useState(false);
  // Remembers the request's state it started from: once a live update or a reload moves the request on
  // (applied, pushed, back to pending), the button's own waiting gives way to it (SF-T-153).
  const state = footerState(request);
  const [waiting, setWaiting] = useState<{ kind: 'apply' | 'reject'; from: string } | null>(null);
  const busy = waiting?.from === state ? waiting.kind : null;
  const setBusy = (kind: 'apply' | 'reject' | null) => setWaiting(kind ? { kind, from: state } : null);
  const open = request.status === 'pending';

  /** Apply (or Push again) with the browser's time limit: the request it answers, else null (said already). */
  const applyTimed = async (failed: string): Promise<PatchRequest | null> => {
    setBusy('apply');
    const out = await withTimeLimit((signal) => applyTaskPatchRequest(taskId, request.id, { signal }), PATCH_APPLY_TIME_LIMIT_MS);
    if (out.timedOut) {
      // Not a failure: it may well have landed. Stop waiting now, then say where it stands.
      setWaiting(null);
      toast.info(timedOutNote([request.id], await reloadWithin(reload)));
      return null;
    }
    setWaiting(null);
    if (out.res.data) {
      onChanged(out.res.data.patch_request);
      return out.res.data.patch_request;
    }
    toast.error(describeApiError(out.res, failed));
    return null;
  };

  if (request.retryable && mayDecide) {
    const retry = async () => {
      if (await applyTimed("Couldn't apply the patch")) toast.success('Applied: the AI builder carries on');
    };
    return (
      <>
        <span className="mr-auto text-xs text-muted-foreground">The machine didn't answer the last Apply. Apply again checks whether it landed, and never applies it twice.</span>
        <Button type="button" variant="outline" disabled={busy !== null} onClick={onClose}>
          Close
        </Button>
        <Button type="button" loading={busy === 'apply'} loadingText="Applying…" onClick={() => void retry()}>
          Apply again
        </Button>
      </>
    );
  }

  if (unpushed(request) && mayDecide) {
    const push = async () => {
      const done = await applyTimed("Couldn't push the patch");
      if (!done) return;
      if (done.pushed) toast.success('Pushed');
      else toast.error(done.error ?? "It still couldn't be pushed");
    };
    return (
      <>
        <span className="mr-auto text-xs text-muted-foreground">It's committed in the run's worktree, but not on GitHub yet.</span>
        <Button type="button" variant="outline" disabled={busy !== null} onClick={onClose}>
          Close
        </Button>
        <Button type="button" loading={busy === 'apply'} loadingText="Pushing…" onClick={() => void push()}>
          Push again
        </Button>
      </>
    );
  }

  if (!open || !mayDecide) {
    return (
      <>
        {open ? <span className="mr-auto text-xs text-muted-foreground">An admin applies or rejects it.</span> : null}
        <Button type="button" variant="outline" onClick={onClose}>
          Close
        </Button>
      </>
    );
  }

  const apply = async () => {
    const done = await applyTimed("Couldn't apply the patch");
    if (done) toast.success(done.pushed ? 'Applied and pushed: the AI builder carries on' : 'Applied, but not pushed yet: the AI builder pushes it next time');
  };
  const reject = async (note: string) => {
    setBusy('reject');
    const res = await rejectTaskPatchRequest(taskId, request.id, note);
    setBusy(null);
    if (res.data) {
      onChanged(res.data.patch_request);
      setRejecting(false);
      toast.success('Rejected: the AI builder carries on without it');
      return;
    }
    toast.error(describeApiError(res, "Couldn't reject the patch"));
  };

  if (rejecting) {
    return <RejectNoteForm busy={busy === 'reject'} onCancel={() => setRejecting(false)} onSubmit={(note) => void reject(note)} />;
  }

  return (
    <>
      <Button type="button" variant="outline" disabled={busy !== null} onClick={() => setRejecting(true)}>
        Reject…
      </Button>
      <Button
        type="button"
        loading={busy === 'apply'}
        loadingText="Applying…"
        disabled={stale}
        disabledReason={stale ? 'It no longer applies to the branch: reject it, or check again' : null}
        onClick={() => void apply()}
      >
        Apply and push
      </Button>
    </>
  );
}

/** A task's patch requests as rows, each with an Open button (the task pop-up's list). */
export function PatchRequestList({ requests, onOpen }: { requests: PatchRequest[]; onOpen: (id: string) => void }) {
  return (
    <ul className="divide-y rounded-md border">
      {ordered(requests).map((p) => (
        <li key={p.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2 text-sm">
          <FileDiff className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0 flex-1 truncate font-mono text-xs" data-hint={p.files.join(', ')}>
            {p.files[0] ?? 'patch'}
            {p.files.length > 1 ? ` +${p.files.length - 1}` : ''}
          </span>
          <StatusBadge size="sm" meta={unpushed(p) ? { label: 'Not pushed', tone: 'warning' } : PATCH_STATUS_META[p.status]} />
          <span className="text-xs text-muted-foreground">
            {p.run_number ? `R-${p.run_number} · ` : ''}
            {formatRelative(p.created_at)}
          </span>
          <Button type="button" size="xs" variant="outline" onClick={() => onOpen(p.id)}>
            Open
          </Button>
        </li>
      ))}
    </ul>
  );
}

/**
 * The task pop-up's patch requests (T-57): every open request on the task and the newest decided ones,
 * as PatchRequestList, each opening PatchRequestDialog on it. Nothing shows on a task without any; a
 * failed load shows LoadError with Retry when the card says some are waiting.
 */
export function TaskPatchRequestsSection({
  taskId,
  version,
  pending = 0,
  preview,
}: {
  taskId: string;
  version: string;
  pending?: number;
  /** Show these instead of loading the task's (the /dev/ui gallery). */
  preview?: PatchRequest[];
}) {
  const projectId = useCurrentProject()?.id;
  // The task's version changes with each decision and live update, so the list reads again.
  const loader = useLoader((signal) => getTaskPatchRequests(taskId, { signal }), [taskId, version], {
    key: `${projectId ?? 'none'}:task-patch-list:${taskId}:${version}`,
    staleMs: 30_000,
    enabled: !preview,
  });
  const [openId, setOpenId] = useState<string | null | undefined>(undefined);
  // Admins decide every project's: the Inbox lists the ones on their own tasks (SF-T-280).
  const isAdmin = useIsAdmin();
  const list = preview ?? loader.data?.data?.patch_requests ?? [];
  const failed = !preview && loader.data?.error ? describeApiError(loader.data, 'Try again in a moment.') : null;
  if (!list.length && !(failed && pending > 0)) return null;
  return (
    <section className="space-y-2" aria-label="Patch requests">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold">Patch requests</h3>
        {isAdmin ? (
          <Button asChild size="xs" variant="link" className="ml-auto">
            <Link to="/inbox?kind=patch_request">Your waiting patches</Link>
          </Button>
        ) : null}
      </div>
      {failed && !list.length ? (
        <LoadError compact title="Couldn't load the patch requests" error={failed} onRetry={loader.reload} />
      ) : (
        <PatchRequestList requests={list} onOpen={setOpenId} />
      )}
      {openId !== undefined ? <PatchRequestDialog taskId={taskId} open initialId={openId} preview={preview} onClose={() => setOpenId(undefined)} /> : null}
    </section>
  );
}
